import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@jotpsych/ui/mui';
import SentryService from '@services/SentryService';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { formatSnoozeDate } from '../components/v2/work/snoozePresets';
import {
  billingV2Api,
  useAddEncounterWorkNoteMutation,
  useDeleteEncounterWorkNoteMutation,
  useEditEncounterWorkNoteMutation,
  useEncounterWorkDispositionMutation,
  V2_TAGS,
} from '../store/v2-api';
import type {
  DeleteWorkNoteArgs,
  EditWorkNoteArgs,
  EncounterWorkAction,
  EncounterWorkNoteError,
  EncounterWorkNoteErrorCode,
  EncounterWorkPending,
  UseEncounterWorkDispositionArgs,
  UseEncounterWorkDispositionResult,
} from '../types/encounterWork';

const FAILURE_COPY: Record<EncounterWorkAction, string> = {
  archive: 'Could not archive. Nothing changed.',
  restore: 'Could not restore. Nothing changed.',
  snooze: 'Could not snooze. Nothing changed.',
};

const OUTCOME: Record<EncounterWorkAction, 'archived' | 'snoozed' | 'restored'> = {
  archive: 'archived',
  restore: 'restored',
  snooze: 'snoozed',
};

/**
 * The one failure with something specific to say. A 409 is not a fault: it
 * means a colleague moved this encounter between the read and the write, and
 * the copy has to say so, because "Could not archive" invites a second press
 * that would fail the same way.
 */
const VERSION_CONFLICT_COPY = 'This claim was updated by someone else. Nothing changed. Try again.';

function hasStatus(error: unknown, status: number): boolean {
  return Boolean(
    error && typeof error === 'object' && 'status' in error && error.status === status,
  );
}

const isVersionConflict = (error: unknown): boolean => hasStatus(error, 409);

/**
 * The `error_code` on a refused note write, when the server sent one.
 * `auth0BaseQuery` wraps every error as `{ status, data: <body>, message }`
 * (`CustomErrorResponse`), so the body is one level down; a bare body is read
 * too, for a store that skips the wrapper.
 */
function readNoteErrorCode(error: unknown): EncounterWorkNoteErrorCode | null {
  if (!error || typeof error !== 'object' || !('data' in error)) return null;
  const wrapped = (error as { data?: unknown }).data;
  const body =
    wrapped && typeof wrapped === 'object' && 'data' in wrapped
      ? (wrapped as { data?: unknown }).data
      : wrapped;
  return (body as Partial<EncounterWorkNoteError> | null | undefined)?.error_code ?? null;
}

type NoteChange = 'edit' | 'delete';
type VerbInFlight = NonNullable<EncounterWorkPending>;

const NOTE_FAILURE_COPY: Record<NoteChange, string> = {
  delete: 'Could not delete the note. Nothing changed.',
  edit: 'Could not save the note. Nothing changed.',
};

/**
 * 403 `note_not_editable`. The kebab was offered on this client's clock and
 * the server's said otherwise — the window closed in the seconds between, or
 * the note is not this person's after all. The change has already been rolled
 * back; the copy says WHY, because "Could not save" invites a second press
 * that would fail the same way.
 */
const NOTE_WINDOW_CLOSED_COPY = 'The window for changing this note has closed. Nothing changed.';

/** 409 `stale_note`: the note was edited elsewhere since this page read it. */
const NOTE_STALE_COPY = 'This note was changed elsewhere. Nothing changed. Try again.';

/** 404 `note_not_found`: deleted elsewhere, or never on this encounter. */
const NOTE_GONE_COPY = 'This note is no longer here. Nothing changed.';

/** Undo is on screen for this long; a note that just left the list is fresh in mind. */
const DELETE_UNDO_MS = 6000;

/**
 * THE FIVE VERBS, as one hook (GDN-3380, contract section 7).
 *
 * Archive, Snooze, Add note, Restore, and the Undo that turns the first two
 * back into the third. Home, Claims and the workbench all call this; none of
 * them owns any part of the behaviour, which is the point. Before this, the
 * same two verbs existed as `useClaimAttention` under a wrapper, keyed by
 * CLAIM, writing a lossy blob no queue read back. Everything here is keyed by
 * ENCOUNTER, because a biller who says "not this morning" means the visit, not
 * one of the three claims that visit has produced.
 *
 * WHAT THE HOOK OWNS, so that no caller has to remember it:
 *
 * - The snackbar, including Undo. Undo is not a fourth disposition: archive
 *   and snooze both undo to `restore`, a verb the server already has, so
 *   there is nothing to roll back and no client-side shadow state to keep.
 * - The outcome report. `onOutcome` fires only after the server said yes, and
 *   says WHICH verb happened, so the queue advance rule (GDN-3322) can ask its
 *   own question rather than infer one from whichever button was pressed.
 * - Statsig, with the two allow-listed `_submitted` events and no `_failed`
 *   companion. Failures are not interactions; the server emits the outcome.
 * - The failure snackbar. A verb that did not happen has to say so, or the row
 *   stays put with no explanation and the biller presses again.
 *
 * WHAT IT DOES NOT OWN: the 09:00 instant (`snoozePresets.ts` explains why the
 * client sends a bare date), and the refetch, which is tag invalidation in
 * `v2-api.ts` so that a surface nobody is looking at still comes back right.
 */
export function useEncounterWorkDisposition({
  encounterId,
  claimId,
  source,
  onOutcome,
  subjectLabel,
  expectedVersion,
}: UseEncounterWorkDispositionArgs): UseEncounterWorkDispositionResult {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { billing } = useEventTracking();
  const [dispositionMutation] = useEncounterWorkDispositionMutation();
  const [addNoteMutation] = useAddEncounterWorkNoteMutation();
  const [editNoteMutation] = useEditEncounterWorkNoteMutation();
  const [deleteNoteMutation] = useDeleteEncounterWorkNoteMutation();
  /**
   * Every verb still in flight, in the order they started. `pending` is the
   * newest of them, and it stays non-null until the LAST one lands: a single
   * value that each verb set and cleared for itself let an Undo re-post and a
   * delete overlap, the second to finish clearing the first's flag while its
   * request was still out, and the menus re-arming over a live write.
   */
  const [inFlight, setInFlight] = useState<VerbInFlight[]>([]);
  const begin = useCallback((verb: VerbInFlight) => setInFlight(list => [...list, verb]), []);
  const end = useCallback(
    (verb: VerbInFlight) =>
      setInFlight(list => {
        const at = list.lastIndexOf(verb);
        return at < 0 ? list : [...list.slice(0, at), ...list.slice(at + 1)];
      }),
    [],
  );
  const pending: EncounterWorkPending = inFlight.length ? inFlight[inFlight.length - 1] : null;

  /**
   * The server call the three dispositions share: mutation, tracking, the
   * outcome report, and the failure snackbar. It returns whether it worked so
   * each verb can add its own receipt. The success snackbars stay OUT of here
   * because Undo has to call `restore`, and a `dispose` that built the Undo
   * button itself would have to reach forward to a verb defined below it.
   */
  const dispose = useCallback(
    async (
      action: EncounterWorkAction,
      opts: { untilDate?: string; note?: string } = {},
    ): Promise<boolean> => {
      begin(action);
      /**
       * THE TRY WRAPS THE SERVER CALL AND NOTHING ELSE.
       *
       * Tracking and `onOutcome` used to live inside it, and that made the page
       * lie: `onOutcome` is a consumer callback, the workbench's version calls
       * `refetch()`, and RTK's `refetch` THROWS synchronously once the query has
       * unmounted. Archive, walk away, press Undo in the eight seconds the
       * snackbar is up, and a restore the server had already committed came back
       * as "Could not restore. Nothing changed." A catch around a caller's
       * callback reports the caller's failure as the server's.
       */
      let landed = false;
      try {
        await dispositionMutation({
          body: {
            action,
            claim_id: claimId ?? null,
            ...(opts.untilDate ? { until_date: opts.untilDate } : {}),
            ...(opts.note ? { note: opts.note } : {}),
            ...(expectedVersion === undefined ? {} : { expected_version: expectedVersion }),
          },
          encounterId,
        }).unwrap();
        landed = true;
      } catch (error) {
        const conflict = isVersionConflict(error);
        /* A conflict is someone else's successful write, not this client
           misbehaving, so it does not go to Sentry. Everything else does:
           there is no `_failed` Statsig event to carry it (the suffix is
           dropped by the pipeline), so without this a systematic failure is
           invisible until a biller notices their queue is not shrinking. */
        if (!conflict) {
          SentryService.captureException(error, {
            level: 'error',
            tags: {
              component: 'useEncounterWorkDisposition',
              feature: 'billing',
              operation: action,
            },
            extra: { encounterId, source },
          });
        }
        enqueueSnackbar(conflict ? VERSION_CONFLICT_COPY : FAILURE_COPY[action], {
          variant: 'error',
        });
        /* Re-read on a conflict so the next press carries the version the
           server actually holds, and so the kebab reflects what the colleague
           did. Nothing else refetches on a failed write. */
        if (conflict) {
          dispatch(
            billingV2Api.util.invalidateTags([
              V2_TAGS.CLAIM_WORK,
              { type: V2_TAGS.ENCOUNTER_WORK_NOTES, id: encounterId },
            ]),
          );
        }
      } finally {
        end(action);
      }

      if (!landed) return false;
      billing.workDispositionSubmitted({ action, source });
      /* An archive-with-a-note writes one note row server-side, so it is one
         note event too. The disposition event is not enough on its own: it
         cannot say whether the biller explained themselves. */
      if (opts.note) billing.workNoteSubmitted({ source });
      onOutcome?.(OUTCOME[action]);
      return true;
    },
    [
      begin,
      billing,
      claimId,
      dispatch,
      dispositionMutation,
      encounterId,
      end,
      enqueueSnackbar,
      expectedVersion,
      onOutcome,
      source,
    ],
  );

  const restore = useCallback(async () => {
    if (await dispose('restore')) {
      enqueueSnackbar('Restored.', { variant: 'success' });
    }
  }, [dispose, enqueueSnackbar]);

  /** The success snackbar for a verb that hid something: it carries Undo. */
  const notifyWithUndo = useCallback(
    (message: string) => {
      enqueueSnackbar(message, {
        action: (snackbarKey): ReactNode => (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              closeSnackbar(snackbarKey);
              void restore();
            }}
            sx={{ textTransform: 'none' }}
          >
            Undo
          </Button>
        ),
        /* Longer than the app's default. Undo is only useful while it is on
           screen, and the row it would bring back has just left the list. */
        autoHideDuration: 8000,
        variant: 'success',
      });
    },
    [closeSnackbar, enqueueSnackbar, restore],
  );

  const archive = useCallback(
    async (opts?: { note?: string }) => {
      if (await dispose('archive', { note: opts?.note })) {
        notifyWithUndo(
          subjectLabel
            ? `Archived ${subjectLabel}. Hidden until the payer acts.`
            : 'Archived. Hidden until the payer acts.',
        );
      }
    },
    [dispose, notifyWithUndo, subjectLabel],
  );

  const snooze = useCallback(
    async (opts: { untilDate: string; note?: string }) => {
      if (await dispose('snooze', { note: opts.note, untilDate: opts.untilDate })) {
        notifyWithUndo(`Snoozed until ${formatSnoozeDate(opts.untilDate)}.`);
      }
    },
    [dispose, notifyWithUndo],
  );

  const addNote = useCallback(
    async (opts: { body: string; claimId?: string | null }) => {
      begin('note');
      let landed = false;
      try {
        await addNoteMutation({
          body: {
            body: opts.body,
            claim_id: opts.claimId === undefined ? (claimId ?? null) : opts.claimId,
          },
          encounterId,
        }).unwrap();
        landed = true;
      } catch (error) {
        SentryService.captureException(error, {
          level: 'error',
          tags: { component: 'useEncounterWorkDisposition', feature: 'billing', operation: 'note' },
          extra: { encounterId, source },
        });
        enqueueSnackbar('Could not add the note. Nothing changed.', { variant: 'error' });
      } finally {
        end('note');
      }
      /* Same rule as the dispositions: the caller's own failure is not the
         server's, so the receipt and the event sit outside the try. It
         RESOLVES either way rather than rejecting — the contract's signature is
         `Promise<void>` and every caller fires it with `void`, so a rejection
         here would surface as an unhandled promise rejection rather than as
         anything a biller could act on. The failure snackbar is the report. */
      if (!landed) return;
      billing.workNoteSubmitted({ source });
      enqueueSnackbar('Note added.', { variant: 'success' });
    },
    [addNoteMutation, begin, billing, claimId, encounterId, end, enqueueSnackbar, source],
  );

  /**
   * What a refused edit or delete says. The store has already rolled the
   * optimistic change back by the time this runs; what is left is the report,
   * and, when the server holds something this page does not, the re-read: a
   * conflict, so the list shows what the colleague wrote and the next edit
   * carries the `updated_at` the server holds; a note gone from under this
   * page, so the list stops showing it. The three refusals are the server
   * doing its job and do not go to Sentry.
   */
  const reportNoteFailure = useCallback(
    (change: NoteChange, error: unknown) => {
      const code = readNoteErrorCode(error);
      const stale = code === 'stale_note' || isVersionConflict(error);
      /* By code, not by status: a bare 403 is a permission the server did
         not explain, and "the window has closed" would be a guess. */
      const closed = code === 'note_not_editable';
      const gone = code === 'note_not_found' || hasStatus(error, 404);
      if (!stale && !closed && !gone) {
        SentryService.captureException(error, {
          level: 'error',
          tags: { component: 'useEncounterWorkDisposition', feature: 'billing', operation: change },
          extra: { encounterId, source },
        });
      }
      enqueueSnackbar(
        stale
          ? NOTE_STALE_COPY
          : closed
            ? NOTE_WINDOW_CLOSED_COPY
            : gone
              ? NOTE_GONE_COPY
              : NOTE_FAILURE_COPY[change],
        { variant: 'error' },
      );
      if (stale || gone) {
        dispatch(
          billingV2Api.util.invalidateTags([
            { type: V2_TAGS.ENCOUNTER_WORK_NOTES, id: encounterId },
          ]),
        );
      }
    },
    [dispatch, encounterId, enqueueSnackbar, source],
  );

  /**
   * Edit in place (contract v1.5). The new body is on the page before the
   * request leaves (`v2-api.ts`), so there is no success receipt: the note
   * itself, now marked "edited", is the receipt. `expected_updated_at` is the
   * note as this page last read it, which is what lets the server refuse to
   * overwrite an edit made elsewhere.
   */
  const editNote = useCallback(
    async (opts: EditWorkNoteArgs) => {
      begin('edit');
      let landed = false;
      try {
        await editNoteMutation({
          encounterId,
          encounterWorkNoteEditRequest: {
            body: opts.body,
            expected_updated_at: opts.expectedUpdatedAt,
          },
          noteId: opts.noteId,
        }).unwrap();
        landed = true;
      } catch (error) {
        reportNoteFailure('edit', error);
      } finally {
        end('edit');
      }
      if (!landed) return;
      billing.workNoteEdited({ source });
    },
    [begin, billing, editNoteMutation, encounterId, end, reportNoteFailure, source],
  );

  /**
   * Delete, immediately and without a dialog (contract v1.5): the note leaves
   * the list as the request goes out, and the receipt carries Undo.
   *
   * UNDO IS NOT AN UN-DELETE. By the time the snackbar is up the server has
   * dropped the row, and the contract has no restore for a note. Undo re-posts
   * the body as a NEW note — a new id, the current time, a fresh window, this
   * person as author, at the foot of the thread, on the claim the old one was
   * on — which is the honest version of "bring it back", and goes through
   * `addNote` so it is reported and received exactly as any other note is.
   */
  const deleteNote = useCallback(
    async (opts: DeleteWorkNoteArgs) => {
      begin('delete');
      let landed = false;
      try {
        await deleteNoteMutation({ encounterId, noteId: opts.noteId }).unwrap();
        landed = true;
      } catch (error) {
        reportNoteFailure('delete', error);
      } finally {
        end('delete');
      }
      if (!landed) return;
      billing.workNoteDeleted({ source });
      enqueueSnackbar('Note deleted', {
        action: (snackbarKey): ReactNode => (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              closeSnackbar(snackbarKey);
              void addNote({ body: opts.body, claimId: opts.claimId });
            }}
            sx={{ textTransform: 'none' }}
          >
            Undo
          </Button>
        ),
        autoHideDuration: DELETE_UNDO_MS,
        variant: 'success',
      });
    },
    [
      addNote,
      begin,
      billing,
      closeSnackbar,
      deleteNoteMutation,
      encounterId,
      end,
      enqueueSnackbar,
      reportNoteFailure,
      source,
    ],
  );

  return useMemo(
    () => ({ addNote, archive, deleteNote, editNote, pending, restore, snooze }),
    [addNote, archive, deleteNote, editNote, pending, restore, snooze],
  );
}
